<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import { usePolicy } from 'dashboard/composables/usePolicy';

const emit = defineEmits([
  'add',
  'import',
  'export',
  'downloadTemplate',
  'basicImport',
  'basicExport',
]);

const { t } = useI18n();
const { checkPermissions } = usePolicy();

const showActionsDropdown = ref(false);
const showAdvancedMenu = ref(false);
const canManageContacts = computed(() =>
  checkPermissions(['administrator', 'contact_manage'])
);

const basicMenuItems = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT'),
    action: 'add',
    value: 'add',
    icon: 'i-lucide-plus',
  },
  ...(canManageContacts.value
    ? [
        {
          label: t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.BASIC_IMPORT'
          ),
          action: 'basicImport',
          value: 'basicImport',
          icon: 'i-lucide-upload',
        },
        {
          label: t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.DOWNLOAD_TEMPLATE'
          ),
          action: 'downloadTemplate',
          value: 'downloadTemplate',
          icon: 'i-lucide-file-down',
        },
        {
          label: t(
            'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.BASIC_EXPORT'
          ),
          action: 'basicExport',
          value: 'basicExport',
          icon: 'i-lucide-download',
        },
        {
          label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADVANCED'),
          action: 'advanced',
          value: 'advanced',
          icon: 'i-lucide-settings-2',
        },
      ]
    : []),
]);

const advancedMenuItems = computed(() => [
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.BACK'),
    action: 'back',
    value: 'back',
    icon: 'i-lucide-arrow-left',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.IMPORT_CONTACT'),
    action: 'import',
    value: 'import',
    icon: 'i-lucide-upload',
  },
  {
    label: t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.EXPORT_CONTACT'),
    action: 'export',
    value: 'export',
    icon: 'i-lucide-download',
  },
]);

const contactMenuItems = computed(() =>
  showAdvancedMenu.value ? advancedMenuItems.value : basicMenuItems.value
);

const closeActions = () => {
  showActionsDropdown.value = false;
  showAdvancedMenu.value = false;
};

const toggleActions = () => {
  showActionsDropdown.value = !showActionsDropdown.value;
  if (!showActionsDropdown.value) showAdvancedMenu.value = false;
};

const handleContactAction = ({ action }) => {
  if (action === 'advanced') {
    setTimeout(() => {
      showAdvancedMenu.value = true;
    }, 0);
    return;
  }
  if (action === 'back') {
    setTimeout(() => {
      showAdvancedMenu.value = false;
    }, 0);
    return;
  }

  if (action === 'add') {
    emit('add');
  } else if (action === 'import') {
    emit('import');
  } else if (action === 'export') {
    emit('export');
  } else if (action === 'downloadTemplate') {
    emit('downloadTemplate');
  } else if (action === 'basicImport') {
    emit('basicImport');
  } else if (action === 'basicExport') {
    emit('basicExport');
  }
  closeActions();
};
</script>

<template>
  <div v-on-clickaway="closeActions" class="relative">
    <Button
      icon="i-lucide-ellipsis-vertical"
      color="slate"
      variant="ghost"
      size="sm"
      :class="showActionsDropdown ? 'bg-n-alpha-2' : ''"
      @click="toggleActions"
    />
    <DropdownMenu
      v-if="showActionsDropdown"
      :key="showAdvancedMenu ? 'advanced' : 'basic'"
      :menu-items="contactMenuItems"
      class="ltr:right-0 rtl:left-0 mt-1 w-72 top-full"
      @action="handleContactAction($event)"
    >
      <template #trailing-icon="{ item }">
        <span
          v-if="item.action === 'advanced'"
          class="i-lucide-chevron-right size-4 ml-auto text-n-slate-9"
        />
      </template>
    </DropdownMenu>
  </div>
</template>
